/*
 * Purpose: LLM-backed outcome planner (OpenAI) with structured JSON output
 * Last modified: 2026-07-10 | Agent
 * Completeness: 90
 */
import { hasResolvedSecret, resolveSecret } from '@/lib/resolveSecret';
import { BLOCK_REGISTRY } from '@/services/blocks/registry';
import { listConnectorCatalogForPrompt } from '@/services/connectors/library/store';
import { API_CATALOG } from '@/services/autoblocks/connectorCatalog';
import { llmPlanSchema, type LlmPlanPayload } from './planSchema';
import type { OutcomePlan } from './types';
import { readEnvOr } from '@/lib/runtimeEnv';

const genId = () => `plan-${Date.now().toString(36)}`;

const MODEL = readEnvOr('VITE_OPENAI_MODEL', 'gpt-4o-mini');
function buildSystemPrompt(): string {
  const blocks = BLOCK_REGISTRY.map(
    (b) => `- ${b.id} (${b.category}/${b.subtype}): ${b.description}`
  ).join('\n');

  const connectors = listConnectorCatalogForPrompt();
  const apis = API_CATALOG.map((a) => `${a.id}: ${a.baseUrl}`).join('\n');

  return `You are AutoBlocks — an outcome compiler for workflow automation. You OWN all connectors — never reference Make, Zapier, or n8n.

The user's OUTCOME (GOAL block text) is the single source of truth. Derive block categories and each block's role from the goal — never clone or reuse pre-built templates because a keyword like "LinkedIn" appears.

Given a user's desired OUTCOME (not implementation steps), produce a JSON plan that:
1. Interprets the goal in plain language (interpretedGoal)
2. Lists ordered steps using ONLY these block types:
${blocks}
3. For EVERY step include blockIntent (why this block exists for the GOAL), taskOutcome (concrete deliverable), and categoryRole (trigger|fetch|transform|decide|act|notify|store|mcp)
4. MCP / LinkedIn steps MUST use subtype mcp_tool with connectorId ab.mcp, mcpGoal aligned to interpretedGoal, and explicit blockIntent + taskOutcome for MCP server communication. When the goal names a specific service backed by an MCP preset (e.g. ab.mcp.slack, ab.mcp.notion, ab.mcp.github — see library below), set config.mcpServerId to that preset's id (e.g. "slack") instead of "default" so the step targets the right connected server
5. Uses AutoBlocks connector IDs from our library (recommend in connectors[] with id matching ab.*):
${connectors}
6. Uses APIs from: ${apis}
7. Proposes customBlocks (transform or http tools) when no primitive fits
8. Includes dryRunFixture — realistic sample input for the trigger
9. Sets useGmailInbound true when the flow starts from email/Gmail/job alerts/receipts

Rules:
- Do NOT set matchedTemplateId — missions assemble from goal-derived steps only
- Never ask the user to use external automation products
- Never ask the user to write code; embed transform logic in step config.transform strings
- Use fact-only AI prompts for resume/cover letter steps
- List assumptions and gaps honestly (API limits, OAuth, CORS, LinkedIn ToS)
- In gaps[], distinguish mission-specific unknowns (scoring rubrics, resume rules, user preferences) from infra — those are knowledge gaps the user fills via the mission library, not Setup
- You may honestly devise ANY approach needed to complete the Mission — novel connectors, steps, APIs, or patterns. Tag each step with riskLevel: "safe" | "elevated" | "critical". Safe = planning/dry-run only. Elevated = credentials, external writes, unverified custom connectors. Critical = deploy, spend, bulk PII export, live side effects. Humans approve elevated/critical before use.
- confidence 0-1 based on how well the outcome maps to available blocks
- Return ONLY valid JSON matching the schema fields
- If MISSION DOSSIER is provided, read it as immutable audit history — build on prior AI work, never contradict recorded entries
- If CURRENT MAP is provided, assess existing blocks and links before planning — refine rather than ignore the wired flow`;
}

export function hasLlmPlannerKey(): boolean {
  return hasResolvedSecret('OPENAI_API_KEY');
}

export async function planFromLlm(
  rawOutcome: string,
  options?: {
    missionKnowledgeContext?: string;
    missionDossierContext?: string;
    mapContext?: string;
  }
): Promise<OutcomePlan | null> {
  const apiKey = resolveSecret('OPENAI_API_KEY');
  if (!apiKey) return null;

  const knowledgeBlock = options?.missionKnowledgeContext?.trim()
    ? `\n\nMISSION KNOWLEDGE (user-provided — treat as ground truth for this mission):\n${options.missionKnowledgeContext.trim()}\n`
    : '';
  const dossierBlock = options?.missionDossierContext?.trim()
    ? `\n\n${options.missionDossierContext.trim()}\n`
    : '';
  const mapBlock = options?.mapContext?.trim()
    ? `\n\n${options.mapContext.trim()}\n\nWhen MISSION CONTEXT is provided, the GOAL block outcome is SSOT. Fulfillment blocks exist to achieve that goal. The user is editing via chat — produce a FULL updated plan (all steps[]) that:
- Preserves step.id = existing nodeId when editing a fulfillment block in place (update blockIntent, taskOutcome, mcpGoal, config)
- Adds new steps with new ids for new blocks
- Removes steps only when the user asked to drop functionality
- Never clones templates by keyword — derive from GOAL\n`
    : '';

  const userPrompt = `OUTCOME / REQUEST:\n${rawOutcome}${knowledgeBlock}${dossierBlock}${mapBlock}\n\nReturn JSON with keys: interpretedGoal, confidence, useGmailInbound?, steps[] (each with blockIntent, taskOutcome, categoryRole), connectors[], apis[], customBlocks[], assumptions[], gaps[], dryRunFixture{}`;

  let response: Response;
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    // Forward the user's API key only when the server doesn't have its own.
    // The server proxies the call to OpenAI so the key never appears in
    // browser DevTools network traces against api.openai.com.
    if (apiKey) headers['X-Api-Key'] = apiKey;
    response = await fetch('/api/llm/plan', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model: MODEL,
        temperature: 0.2,
        response_format: { type: 'json_object' },
        messages: [
          { role: 'system', content: buildSystemPrompt() },
          { role: 'user', content: userPrompt },
        ],
      }),
    });
  } catch {
    return null;
  }

  if (!response.ok) {
    console.warn('[llmPlanner] OpenAI error', response.status);
    return null;
  }

  const data = (await response.json()) as {
    choices?: Array<{ message?: { content?: string } }>;
  };
  const content = data.choices?.[0]?.message?.content;
  if (!content) return null;

  let parsed: LlmPlanPayload;
  try {
    parsed = llmPlanSchema.parse(JSON.parse(content));
  } catch (e) {
    console.warn('[llmPlanner] Invalid plan JSON', e);
    return null;
  }

  return {
    id: genId(),
    rawOutcome: rawOutcome.trim(),
    interpretedGoal: parsed.interpretedGoal,
    confidence: parsed.confidence,
    steps: parsed.steps,
    connectors: parsed.connectors.map((c) => ({
      ...c,
      docsUrl: c.docsUrl || 'https://developers.google.com/gmail/api',
    })),
    apis: parsed.apis,
    customBlocks: parsed.customBlocks,
    assumptions: parsed.assumptions,
    gaps: parsed.gaps,
    dryRunFixture: parsed.dryRunFixture,
    plannerSource: 'llm',
    useGmailInbound: parsed.useGmailInbound,
  };
}
